import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  getSearchConversationsApiV1ConversationsSearchGetQueryKey,
  searchConversationsApiV1ConversationsSearchGet,
} from '#/shared/api/generated/communication/theCargoCommunication'
import type {
  SearchConversationsApiV1ConversationsSearchGetParams,
  SearchSection,
} from '#/shared/api/generated/communication/models'

import { conversationToActivityItem } from '../lib/map-conversation-list'
import { messageHitToRow } from '../lib/map-message-hit'
import type { MessageHitRow } from '../lib/map-message-hit'
import { usePhoneSearchTerm } from './search'
import type { PhoneActivityItem } from './types'

/** Chats shown inline before the user asks to see the rest. */
const CHAT_SIZE = 5
/** Chats shown after "see all" — the endpoint's ceiling, which is well past
 *  what a search box needs before the term should just be narrowed. */
const CHAT_SIZE_EXPANDED = 50
/** Message hits per page; more load as the results are scrolled. */
const PAGE_SIZE = 20
/** Results are re-queried on every keystroke anyway (the term is in the key);
 *  this only stops a refetch when the same term is revisited straight away. */
const STALE_TIME_MS = 30_000

export interface ConversationSearchResult {
  /** Threads whose counterparty matches — the top slice, newest activity first. */
  chats: ReadonlyArray<PhoneActivityItem>
  /** More matching threads exist than `chats` holds. */
  hasMoreChats: boolean
  /** Message/transcript matches across every visible thread, newest first. */
  messages: ReadonlyArray<MessageHitRow>
  isLoading: boolean
  isError: boolean
  /** Older message hits remain to load. */
  hasMore: boolean
  isFetchingMore: boolean
  loadMore: () => void
}

interface SearchOptions {
  /** Restrict the message hits to one thread — the in-thread search. The server
   *  then omits the chats section entirely (see `chats` below). */
  conversationId?: string
  /** Which sections to compute; defaults to both. */
  section?: SearchSection
  /** How many chats to return before `hasMoreChats` is set. */
  chatSize?: number
}

const EMPTY_RESULT: ConversationSearchResult = {
  chats: [],
  hasMoreChats: false,
  messages: [],
  isLoading: false,
  isError: false,
  hasMore: false,
  isFetchingMore: false,
  loadMore: () => {},
}

/**
 * Split search over the phone workspace: `chats` matches the counterparty
 * (name / company / number), `messages` runs full-text over message bodies and
 * call transcripts. Only the `messages` section pages — the server omits the
 * `chats` key entirely once a cursor is set (and for a thread-scoped or
 * `section=messages` search), so the chat slice is read off the first page.
 *
 * `query` is the already-debounced term (see `usePhoneSearchTerm`); pass
 * undefined to keep the query idle.
 */
export function useConversationSearch(
  query: string | undefined,
  enabled: boolean,
  options: SearchOptions = {},
): ConversationSearchResult {
  const { t } = useTranslation()
  const { conversationId, section, chatSize = CHAT_SIZE } = options
  const params: SearchConversationsApiV1ConversationsSearchGetParams = {
    q: query ?? '',
    section,
    conversation_id: conversationId,
    chat_size: chatSize,
    size: PAGE_SIZE,
  }
  const active = enabled && !!query

  const search = useInfiniteQuery({
    queryKey: [
      ...getSearchConversationsApiV1ConversationsSearchGetQueryKey(params),
      'infinite',
    ],
    queryFn: ({ pageParam, signal }) =>
      searchConversationsApiV1ConversationsSearchGet(
        { ...params, cursor: pageParam },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.data.next_cursor ?? undefined,
    enabled: active,
    staleTime: STALE_TIME_MS,
  })

  const pages = search.data?.pages
  const chats = useMemo(
    () =>
      (pages?.[0]?.data.chats ?? []).map((chat) =>
        conversationToActivityItem(chat, t),
      ),
    [pages, t],
  )
  const messages = useMemo(
    () =>
      (pages ?? []).flatMap((page) =>
        (page.data.messages ?? []).map((hit) => messageHitToRow(hit, t)),
      ),
    [pages, t],
  )

  if (!active) return EMPTY_RESULT
  return {
    chats,
    hasMoreChats: pages?.[0]?.data.has_more_chats ?? false,
    messages,
    isLoading: search.isLoading,
    isError: search.isError,
    hasMore: search.hasNextPage,
    isFetchingMore: search.isFetchingNextPage,
    loadMore: () => void search.fetchNextPage(),
  }
}

interface PhoneSearchResults {
  /** The term being searched, or undefined when the box is empty/too short. */
  term: string | undefined
  /** The split results replace the activity feed. */
  active: boolean
  result: ConversationSearchResult
  /** The chats section is showing its full slice. */
  chatsExpanded: boolean
  expandChats: () => void
}

/**
 * The phone panel's search state: the debounced term plus its split results.
 * `enabled` gates the request the same way the activity feed is gated, so
 * nothing is fetched behind a closed panel or a covering sub-view.
 */
export function usePhoneSearchResults(enabled: boolean): PhoneSearchResults {
  const term = usePhoneSearchTerm()
  const [chatsExpanded, setChatsExpanded] = useState(false)
  // A new term starts collapsed again — "see all" applies to one search.
  useEffect(() => setChatsExpanded(false), [term])
  const result = useConversationSearch(term, enabled, {
    chatSize: chatsExpanded ? CHAT_SIZE_EXPANDED : CHAT_SIZE,
  })
  return {
    term,
    active: enabled && term !== undefined,
    result,
    chatsExpanded,
    expandChats: useCallback(() => setChatsExpanded(true), []),
  }
}
